import React, { useEffect, useRef, useState } from 'react';
import { BarChart2, FileDown, Users, Sparkles, Image as ImageIcon, Target, CheckCircle, ArrowRight, Zap, RefreshCw, Link } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import PublicNavbar from '@/components/landing/PublicNavbar';
import PublicFooter from '@/components/landing/PublicFooter';
import { useI18n } from '@/lib/I18nContext';

/* ─── Data ─────────────────────────────────────────────────── */
/* ─── Data (estática, sin texto — el texto real se arma adentro del componente) ─── */
const FEATURES_BASE = [
{ icon: BarChart2, key: 'realtime_dashboard' },
{ icon: Target, key: 'scorecard' },
{ icon: Sparkles, key: 'ai_analysis' },
{ icon: ImageIcon, key: 'creatives' },
{ icon: Users, key: 'multi_client' },
{ icon: FileDown, key: 'pdf_export' }];


const STEPS_BASE = [
{ n: '01', icon: Link, key: 'connect' },
{ n: '02', icon: RefreshCw, key: 'sync' },
{ n: '03', icon: FileDown, key: 'export' }];


const PLAN_INCLUDES_KEYS = [
'unlimited_clients', 'realtime_dashboard', 'monthly_scorecard',
'ai_analysis', 'pdf_export', 'organic_instagram',
'creatives_metrics', 'priority_support'];


const FAQ_KEYS = ['1', '2', '3', '4', '5', '6'];

const STATS_BASE = [
{ end: 10000, suffix: '+', key: 'campaigns_analyzed' },
{ end: 500, suffix: '+', key: 'reports_generated' },
{ end: 98, suffix: '%', key: 'satisfaction' }];


/* ─── Hooks ─────────────────────────────────────────────────── */
function useCountUp(end, suffix, duration = 1800, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, end, duration]);
  return val.toLocaleString('es-CL') + suffix;
}

function useIntersection(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {if (e.isIntersecting) setVisible(true);}, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

/* ─── Particle canvas ───────────────────────────────────────── */
function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Leemos offsetWidth/offsetHeight ANTES de escribir canvas.width/height —
    // si se lee después de escribir, se fuerza un recalculo de layout sincrónico
    // innecesario (forced reflow), que es justo lo que marcaba el análisis de PageSpeed.
    let W = canvas.offsetWidth;
    let H = canvas.offsetHeight;
    canvas.width = W;
    canvas.height = H;
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 1.5 + 0.5,
      dx: (Math.random() - 0.5) * 0.3, dy: (Math.random() - 0.5) * 0.3,
      o: Math.random() * 0.4 + 0.1
    }));
    let raf;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(107,140,255,${p.o})`;
        ctx.fill();
        p.x += p.dx;p.y += p.dy;
        if (p.x < 0) p.x = W;if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;if (p.y > H) p.y = 0;
      });
      raf = requestAnimationFrame(draw);
    }
    draw();
    const onResize = () => {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W;
      canvas.height = H;
    };
    window.addEventListener('resize', onResize);
    return () => {cancelAnimationFrame(raf);window.removeEventListener('resize', onResize);};
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} />;
}

/* ─── Typing effect ─────────────────────────────────────────── */
function TypingText({ text, delay = 0 }) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed(text.slice(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, 45);
    return () => clearInterval(interval);
  }, [started, text]);
  return <>{displayed}<span className="animate-pulse">|</span></>;
}

/* ─── Stat counter ──────────────────────────────────────────── */
function StatCounter({ end, suffix, label, start }) {
  const val = useCountUp(end, suffix, 1800, start);
  return (
    <div className="text-center">
      <p className="font-heading font-bold text-4xl" style={{ color: '#00D5FF' }}>{val}</p>
      <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>{label}</p>
    </div>);

}

/* ─── Feature card ──────────────────────────────────────────── */
function FeatureCard({ icon: Icon, title, desc, delay }) {
  const ref = useRef(null);
  const visible = useIntersection(ref);
  return (
    <div
      ref={ref}
      className="group rounded-2xl p-6 cursor-default"
      style={{
        background: 'linear-gradient(135deg, #111827 0%, #1a2235 100%)',
        borderLeft: '3px solid #00D5FF',
        border: '1px solid #1E2D45',
        borderLeftWidth: '3px',
        borderLeftColor: '#00D5FF',
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms, box-shadow 0.2s ease`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)'
      }}>
      
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>
        
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="font-heading font-semibold mb-2" style={{ color: '#F8FAFC' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>{desc}</p>
    </div>);

}

/* ─── Step card ─────────────────────────────────────────────── */
function StepCard({ n, icon: Icon, title, desc, delay, isMiddle }) {
  const ref = useRef(null);
  const visible = useIntersection(ref);
  return (
    <div
      ref={ref}
      className="relative rounded-2xl p-8 flex flex-col items-center text-center"
      style={{
        background: '#111827',
        border: '1px solid #1E2D45',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)'
      }}>
      
      <div className="text-7xl font-heading font-bold mb-4" style={{ color: 'rgba(0,213,255,0.06)', lineHeight: 1 }}>{n}</div>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>
        <Icon className={`w-5 h-5 text-white ${isMiddle ? 'animate-spin' : ''}`} style={isMiddle ? { animationDuration: '3s' } : {}} />
      </div>
      <h3 className="font-heading font-semibold mb-2" style={{ color: '#F8FAFC' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>{desc}</p>
    </div>);

}

/* ─── Checklist item ────────────────────────────────────────── */
function CheckItem({ text, delay, start }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!start) return;
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [start, delay]);
  return (
    <div
      className="flex items-center gap-3"
      style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: show ? 1 : 0, transform: show ? 'translateX(0)' : 'translateX(-12px)' }}>
      
      <CheckCircle className="w-4 h-4 shrink-0" style={{ color: '#22C55E' }} />
      <span className="text-sm" style={{ color: '#94A3B8' }}>{text}</span>
    </div>);

}

/* ─── Main component ────────────────────────────────────────── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#111827', border: '1px solid #1E2D45' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
        
        <span className="font-heading font-semibold text-sm sm:text-base" style={{ color: '#F8FAFC' }}>{q}</span>
        <span className="shrink-0 text-lg" style={{ color: '#00D5FF' }}>{open ? '−' : '+'}</span>
      </button>
      {open &&
      <p className="px-5 pb-4 text-sm leading-relaxed" style={{ color: '#94A3B8' }}>{a}</p>
      }
    </div>);

}

/* Main component */
export default function Landing() {
  const { t } = useI18n();

  const FEATURES = FEATURES_BASE.map((f) => ({
    icon: f.icon,
    title: t(`landing.feature_${f.key}_title`, f.key),
    desc: t(`landing.feature_${f.key}_desc`, '')
  }));

  const STEPS = STEPS_BASE.map((s) => ({
    n: s.n,
    icon: s.icon,
    title: t(`landing.step_${s.key}_title`, s.key),
    desc: t(`landing.step_${s.key}_desc`, '')
  }));

  const PLAN_INCLUDES = PLAN_INCLUDES_KEYS.map((k) => t(`landing.plan_include_${k}`, k));

  const STATS = STATS_BASE.map((s) => ({
    end: s.end,
    suffix: s.suffix,
    label: t(`landing.stat_${s.key}`, s.key)
  }));

  const FAQS = FAQ_KEYS.map((k) => ({
    q: t(`landing.faq_${k}_q`, ''),
    a: t(`landing.faq_${k}_a`, '')
  }));

  // Inyecta/actualiza el schema FAQPage (JSON-LD) reflejando las preguntas en el
  // idioma actual — ayuda a que buscadores y motores de IA lean directamente las
  // preguntas y respuestas sin tener que interpretar el HTML visual.
  useEffect(() => {
    const id = 'faq-jsonld';
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.filter((f) => f.q && f.a).map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a }
      }))
    });
    return () => {script?.remove();};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const statsRef = useRef(null);
  const statsVisible = useIntersection(statsRef, 0.3);
  const pricingRef = useRef(null);
  const pricingVisible = useIntersection(pricingRef, 0.2);

  const goLogin = () => base44.auth.redirectToLogin();
  const goRegister = () => base44.auth.redirectToLogin();

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen font-body" style={{ background: '#070B14', color: '#F8FAFC', overflowX: 'hidden', maxWidth: '100vw', width: '100%' }}>
      <PublicNavbar />

      <main>
      {/* ── Hero ── */}
      <section
          className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-16 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #070B14 0%, #0d1525 50%, #111827 100%)', width: '100%' }}>
          
        <style>{`
          @keyframes gradientCycle {
            0%   { background-position: 0% 50%; }
            50%  { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50%       { transform: translateY(-6px); }
          }
          @keyframes shimmer {
            0%   { background-position: -200% center; }
            100% { background-position:  200% center; }
          }
          .shimmer-border {
            background: linear-gradient(90deg, #00D5FF 0%, #7C3AED 50%, #00D5FF 100%);
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
        `}</style>

        {/* Glow effects */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(0,213,255,0.15) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 100% 100%, rgba(124,58,237,0.15) 0%, transparent 60%)' }} />
        <ParticleCanvas />

        <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-2">
          <div
              className="inline-flex items-center gap-2 text-xs font-semibold rounded-full border max-w-full mb-6 mt-2 px-4"
              style={{ background: 'rgba(0,213,255,0.1)', borderColor: 'rgba(0,213,255,0.3)', color: '#00D5FF', animation: 'fadeInDown 0.6s ease forwards', opacity: 0, animationFillMode: 'forwards', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              
            <Zap className="w-3.5 h-3.5" />
            {t('landing.hero_badge', 'Para agencias y freelancers de Meta Ads')}
          </div>

          <h1 className="font-heading font-bold text-white mb-4" style={{ fontSize: 'clamp(1.75rem, 8vw, 3.5rem)', lineHeight: 1.15 }}>
            {t('landing.hero_title')}
          </h1>
          <h1 className="font-heading font-bold mb-6" style={{ fontSize: 'clamp(1.75rem, 8vw, 3.5rem)', lineHeight: 1.15, color: '#00D5FF' }}>
            <TypingText text={t('landing.hero_title_typed', 'en minutos, no en horas.')} delay={400} />
          </h1>

          <p
              className="text-lg mb-10 max-w-2xl mx-auto leading-relaxed"
              style={{ color: '#94A3B8', animation: 'fadeIn 0.8s ease 0.5s forwards', opacity: 0, animationFillMode: 'forwards' }}>
              
            {t('landing.hero_subtitle')}
          </p>

          <div
              className="flex flex-col sm:flex-row gap-3 justify-center w-full"
              style={{ animation: 'fadeInUp 0.7s ease 0.7s forwards', opacity: 0, animationFillMode: 'forwards' }}>
              
            <button
                onClick={goRegister}
                className="flex items-center justify-center gap-2 font-bold rounded-xl transition-all hover:scale-[1.03] active:scale-[0.98] w-full sm:w-auto"
                style={{ background: 'linear-gradient(135deg, #22D3FF 0%, #00D5FF 60%, #33C7FF 100%)', color: '#070B14', fontSize: '16px', padding: '14px 28px', boxShadow: '0 8px 30px -8px rgba(0,213,255,0.55)' }}>
                
              {t('landing.start_free')}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
                onClick={goLogin}
                className="flex items-center justify-center gap-2 text-white font-semibold text-base px-8 py-4 rounded-xl border border-white/30 hover:border-white/60 transition-all hover:bg-white/5 backdrop-blur-sm w-full sm:w-auto">
                
              {t('landing.watch_demo')}
            </button>
          </div>

          {/* Stats counter */}
          <div ref={statsRef} className="mt-14 w-full max-w-xl mx-auto" style={{ color: '#94A3B8', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            {STATS.map((s) =>
              <StatCounter key={s.label} end={s.end} suffix={s.suffix} label={s.label} start={statsVisible} />
              )}
          </div>
        </div>

        {/* Mock browser frame */}
        <div
            className="relative z-10 mt-16 w-full max-w-4xl mx-auto"
            style={{ animation: 'fadeInUp 1s ease 1s forwards, float 3s ease-in-out 2s infinite', opacity: 0, animationFillMode: 'forwards', overflow: 'hidden', borderRadius: '1.125rem', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,213,255,0.08), 0 0 60px -10px rgba(124,58,237,0.15)' }}>
            
          <div className="bg-gray-800/80 backdrop-blur-md rounded-t-xl border border-white/10 px-4 py-2.5 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-400/70" />
            <div className="w-3 h-3 rounded-full bg-yellow-400/70" />
            <div className="w-3 h-3 rounded-full bg-green-400/70" />
            <div className="flex-1 mx-4 bg-white/10 rounded px-3 py-1 text-xs text-white/40">bezkraen.app/dashboard</div>
          </div>
          <div className="bg-gray-900/60 backdrop-blur-md rounded-b-xl border border-t-0 border-white/10 overflow-hidden" style={{ aspectRatio: '16/7' }}>
            <div className="p-5 grid grid-cols-3 gap-3 h-full">
              {['impressions', 'reach', 'clicks', 'cpc', 'spend', 'roas'].map((key, i) =>
                <div key={key} className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10 transition-all hover:border-cyan-400/30 hover:bg-white/[0.08]">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider">{t('metrics.' + key, key)}</p>
                  <p className="text-white font-bold text-xl mt-1" style={{ fontFamily: 'var(--font-numeric)' }}>{['1.2M', '890K', '34.5K', '$12', '$3.2K', '4.8x'][i]}</p>
                  <p className="text-green-400 text-[10px] mt-1">+{[12, 8, 23, 5, 18, 14][i]}%</p>
                </div>
                )}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
          @keyframes fadeInUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
          @keyframes fadeInDown { from { opacity:0; transform:translateY(-12px) } to { opacity:1; transform:translateY(0) } }
        `}</style>
      </section>

      {/* ── How it works ── */}
      <section id="how" className="py-24 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl mb-3" style={{ color: '#F8FAFC' }}>{t('landing.how_it_works')}</h2>
            <p style={{ color: '#94A3B8' }}>{t('landing.how_it_works_subtitle', 'Sin configuraciones complejas, sin tokens manuales')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) =>
              <StepCard key={s.n} {...s} delay={i * 150} isMiddle={i === 1} />
              )}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-24 px-6" style={{ background: 'linear-gradient(135deg, #070B14 0%, #111827 100%)' }}>
        <div ref={pricingRef} className="max-w-lg mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl mb-3" style={{ color: '#F8FAFC' }}>{t('landing.pricing_title')}</h2>
          <p className="mb-10" style={{ color: '#94A3B8' }}>{t('landing.pricing_subtitle', 'Sin límites ocultos ni funciones bloqueadas')}</p>

          {/* Card with shimmer border */}
          <div className="relative p-[2px] rounded-3xl" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED, #00D5FF)', backgroundSize: '300% 300%', animation: 'gradientCycle 4s ease infinite' }}>
            <div className="rounded-3xl p-8 shadow-2xl" style={{ background: '#111827' }}>
              <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: '#00D5FF' }}>{t('landing.pricing_plan_name', 'Plan Profesional')}</p>
              <div className="flex items-end justify-center gap-1 mb-8">
                <span className="font-heading font-bold text-6xl" style={{ color: '#F8FAFC' }}>$19.99</span>
                <span className="mb-2 text-lg" style={{ color: '#94A3B8' }}>{t('landing.pricing_period', 'USD / mes')}</span>
              </div>
              <div className="space-y-3 mb-8 text-left">
                {PLAN_INCLUDES.map((item, i) =>
                  <CheckItem key={item} text={item} delay={i * 120} start={pricingVisible} />
                  )}
              </div>
              <button
                  onClick={goRegister}
                  className="w-full font-bold py-4 rounded-xl transition-all text-base hover:opacity-90 hover:scale-[1.01]"
                  style={{ background: '#00D5FF', color: '#070B14' }}>
                  
                {t('landing.pricing_free_trial')}
              </button>
              <p className="text-sm mt-3" style={{ color: '#94A3B8' }}>{t('landing.pricing_no_card')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-24 px-6" style={{ background: '#070B14' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl mb-3" style={{ color: '#F8FAFC' }}>{t('landing.features_title')}</h2>
            <p style={{ color: '#94A3B8' }}>{t('landing.features_subtitle', 'Una plataforma completa para gestionar y reportar el performance de tus clientes')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) =>
              <FeatureCard key={f.title} {...f} delay={i * 80} />
              )}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading font-bold text-3xl mb-10 text-center" style={{ color: '#F8FAFC' }}>{t('landing.faq_title')}</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      </main>

      <PublicFooter />
    </div>);

}